import Dexie, { PromiseExtended } from 'dexie';
import db from '../index';
import { makeClearPublisher } from 'serviceWorker/broadcasts/publishers';
import { directoryContract } from 'shared/domain/directory/directoryContract';
import { entityDbClearFactory } from '../entityDbClearFactory';
import { clear as directoriesServiceClear } from './directoriesService';
import { wrapQuery } from '../queryWrapper';
import {
  DirectoryEntity,
  DirectoryInsertEntity,
  DirectoryLocalInsertEntity,
} from 'serviceWorker/repository/directory/entity';
import {
  getErrorMsg,
  isDexieConstraintError,
  logWithRethrow,
  Operation,
} from '../helpers';
import { DirectoryInDto } from 'shared/dtos/in/directory';
import { directoryInDtoToCreateEntity } from 'serviceWorker/repository/directory/mappings';
import { nowISO } from 'shared/utils/date/dates';
import { isSyncStatusPendingDelete } from 'shared/domain/entitySyncStatus/syncStatus';
import { Changes } from 'shared/types/commonEntities';
import {
  makeDefaultCount,
  makeDefaultGetMany,
  makeGetManyUndeleted,
  makeDefaultGetManyByIds,
  makeDefaultGetOne,
  makeDefaultRemoveBatch,
} from '../defaultDaoFactories';
import { LogLevel } from 'shared/types/logger';
import { normalizeForSearch } from 'shared/utils/search';
import { assignRemoteIdFactory } from '../assignRemoteIdFactory';

export const clear = entityDbClearFactory(
  db,
  ['directories', 'directoriesService'],
  () => db.directories.clear(),
  directoriesServiceClear,
  makeClearPublisher(directoryContract)
);

const update = wrapQuery(
  db,
  (
    directoryFindStrategy: () => Promise<DirectoryEntity | undefined>,
    data: DirectoryInsertEntity
  ): Promise<any> => {
    return directoryFindStrategy().then(
      (directory: DirectoryEntity | undefined) => {
        if (!directory) {
          throw new Error('Cannot find directory to update');
        } else {
          const directoryWithLocalData = keepLocalDataOnUnuploadedStatus(
            data,
            directory
          );

          return db.directories.update(
            directory.localId,
            directoryWithLocalData
          );
        }
      }
    );
  }
);

export const quantity = makeDefaultCount<DirectoryEntity>(
  db,
  db.directories
);

export const get = makeDefaultGetMany<DirectoryEntity>(db, db.directories);

export const getUndeleted = makeDefaultGetMany<DirectoryEntity>(
  db,
  db.directories,
  { deleted: 0 }
);

export const getByQuery = makeDefaultGetMany<DirectoryEntity>(
  db,
  db.directories
);

export const getUndeletedByParentId = makeGetManyUndeleted<
  string,
  DirectoryEntity
>(db, db.directories, 'parentId');

export const getByIds = makeDefaultGetManyByIds<number, DirectoryEntity>(
  db,
  db.directories,
  'localId'
);

export const getOneByRemote = makeDefaultGetOne<string, DirectoryEntity>(
  db,
  db.directories,
  '_id'
);

export const getOne = makeDefaultGetOne<number, DirectoryEntity>(
  db,
  db.directories,
  'localId'
);

export const getSearched = wrapQuery(
  db,
  async (searchPhrase: string): Promise<DirectoryEntity[]> => {
    return db.directories
      .toArray()
      .then((dirs) => {
        return dirs.filter((dir: DirectoryEntity) => {
          return normalizeForSearch(dir.name).includes(
            normalizeForSearch(searchPhrase)
          );
        });
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.filter, 'directories'),
          errorObj: e,
          additionalInfo: { query: { searchPhrase } },
        })
      );
  }
);

const upsertBatch = wrapQuery(
  db,
  async (data: DirectoryInDto[]): Promise<number> => {
    if (data.length === 0) return Promise.resolve(0);

    const entities = data
      .map(directoryInDtoToCreateEntity)
      .filter((e): e is DirectoryInsertEntity => Boolean(e._id));

    const entityMap = new Map<string, DirectoryInsertEntity>();
    for (const entity of entities) {
      entityMap.set(entity._id!, entity);
    }
    const uniqueEntities = Array.from(entityMap.values());
    const incomingIds = Array.from(entityMap.keys());

    const existingRecords = await db.directories
      .where('_id')
      .anyOf(incomingIds)
      .toArray();

    const existingMap = new Map<string, number>();
    for (const record of existingRecords) {
      if (record._id) {
        existingMap.set(record._id, record.localId);
      }
    }

    const entitiesToUpsert = uniqueEntities.map((entity) => {
      const existingLocalId = existingMap.get(entity._id!);
      if (existingLocalId !== undefined) {
        return { ...entity, localId: existingLocalId };
      }
      return entity;
    });

    return db.directories
      // @ts-ignore localId may or may not be present
      .bulkPut(entitiesToUpsert)
      .catch((e) => {
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.upsertBatch, 'directories'),
          errorObj: e,
          additionalInfo: { query: { data } },
        });
      });
  }
);

export const fastAddBatch = wrapQuery(
  db,
  async (data: DirectoryInDto[]): Promise<number> => {
    if (data.length === 0) return Promise.resolve(0);

    const entityMap = new Map<string, DirectoryInsertEntity>();
    for (const directory of data) {
      const entity = directoryInDtoToCreateEntity(directory);
      if (entity._id) {
        entityMap.set(entity._id, entity);
      }
    }
    const incomingIds = Array.from(entityMap.keys());

    if (incomingIds.length === 0) return Promise.resolve(0);

    const existingRecords = await db.directories
      .where('_id')
      .anyOf(incomingIds)
      .toArray();

    const existingIdSet = new Set(existingRecords.map((r) => r._id));
    const newEntities = Array.from(entityMap.values()).filter(
      (e) => !existingIdSet.has(e._id!)
    );

    if (newEntities.length === 0) return Promise.resolve(0);

    try {
      return await db.directories
        // @ts-ignore DirectoryEntityToCreate localId is an autoincrement
        .bulkPut(newEntities);
    } catch (e: unknown) {
      if (isDexieConstraintError(e)) {
        return upsertBatch(data);
      }
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.upsertBatch, 'directories'),
        errorObj: e,
        additionalInfo: { query: { data } },
      });
      return 0;
    }
  }
);

export const addBatch = upsertBatch;
export const updateBatch = upsertBatch;

export const addLocal = wrapQuery(
  db,
  (data: DirectoryLocalInsertEntity): PromiseExtended<number> => {
    // @ts-ignore DirectoryLocalInsertEntity localId is an autoincrement
    return db.directories.add(data).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.add, 'directories'),
        errorObj: e,
        additionalInfo: { query: { data } },
      })
    );
  }
);

export const removeBatch = makeDefaultRemoveBatch<string, DirectoryEntity>(
  db,
  db.directories,
  '_id'
);

export const updateOne = wrapQuery(
  db,
  async (
    localId: number,
    changes: Changes<DirectoryEntity>,
    options?: { skipModifyTime: boolean }
  ): Promise<any> => {
    const modifiedAt = options?.skipModifyTime
      ? {}
      : { modifiedAt: nowISO() };
    return db.directories
      .update(localId, { ...changes, ...modifiedAt })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'directories'),
          errorObj: e,
          additionalInfo: { query: { ...changes, localId } },
        })
      );
  }
);

function keepLocalDataOnUnuploadedStatus(
  directoryToInsert: DirectoryInsertEntity,
  existingEntity: DirectoryEntity
): DirectoryInsertEntity {
  if (isSyncStatusPendingDelete(existingEntity.syncStatus)) {
    directoryToInsert.deleted = 1;
  }

  return directoryToInsert;
}

/** The server id of a directory created here; drops a copy a sync stored first (PT-7278). */
export const assignRemoteId = assignRemoteIdFactory(db, 'directories', db.directories);
